import React, { useState, useRef, useEffect } from "react";
import "./FeedbackModal.scss";
import Dropdown from "./dropDown";

const INQUIRY_TYPES = [
  { value: "General Query", label: "General Query" },
  { value: "Reporting an Issue", label: "Reporting an Issue" },
  { value: "Requesting a New Feature", label: "Requesting a New Feature" },
];

const MODULES = [
  { value: "Dashboard", label: "Dashboard" },
  { value: "User Management", label: "User Management" },
  { value: "User Profile", label: "User Profile" },
  { value: "Study Management", label: "Study Management" },
  { value: "eISF Dashboard", label: "eISF Dashboard" },
  { value: "eISF / ePSF", label: "eISF/ePSF" },
  { value: "QMS", label: "QMS" },
  { value: "FAQ", label: "FAQ" },
  { value: "Other", label: "Other" },
];

const FeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialValues = null,
}) => {
  const [formData, setFormData] = useState({
    inquiryType: "",
    module: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [screenshots, setScreenshots] = useState([]);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    // Revoke any object URLs to avoid memory leaks
    setScreenshots((prev) => {
      prev.forEach((shot) => {
        if (shot.preview) {
          URL.revokeObjectURL(shot.preview);
        }
      });
      return [];
    });

    setFormData({ inquiryType: "", module: "", description: "" });
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open: apply optional seed values. Close: clear form for next open.
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }
    if (initialValues && typeof initialValues === "object") {
      setFormData({
        inquiryType: String(initialValues.inquiryType || "").trim(),
        module: String(initialValues.module || "").trim(),
        description: String(initialValues.description || "").trim(),
      });
      setErrors({});
    }
  }, [isOpen, initialValues]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.inquiryType) newErrors.inquiryType = "This field is required";
    if (!formData.module) newErrors.module = "This field is required";
    const isEmpty = !formData.description?.trim();
    if (isEmpty) newErrors.description = "This field is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (isSubmitting) return; // prevent double submit

    // Let parent handle success/error and closing
    await onSubmit?.({ ...formData, screenshots });

    // Parent closes modal on success; if it stays open (error), keep data so user can adjust
    if (!isSubmitting) {
      // Optional: you can decide to reset here only if needed
    }
  };

  const handleClose = () => {
    onClose();
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleScreenshotChange = (e) => {
    const newScreenshots = Array.from(e.target.files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random(),
    }));
    setScreenshots((prev) => [...prev, ...newScreenshots]);
    fileInputRef.current.value = "";
  };

  const handleRemoveScreenshot = (id) => {
    setScreenshots((prev) => {
      const screenshot = prev.find((s) => s.id === id);
      screenshot?.preview && URL.revokeObjectURL(screenshot.preview);
      return prev.filter((s) => s.id !== id);
    });
  };

  const selectedInquiryType = INQUIRY_TYPES.find(
    (t) => t.value === formData.inquiryType
  );
  const selectedModule = MODULES.find((m) => m.value === formData.module);

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal-container">
        <div className="feedback-modal-content">
          {/* Modal Header */}
          <div className="feedback-modal-header">
            <div className="feedback-header-content">
              <h2 className="feedback-modal-title">Contact Support</h2>
              <p className="feedback-modal-subtitle">
                Tell us what you need help with and we’ll get back to you.
              </p>
            </div>
            <button
              type="button"
              className="feedback-close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Modal Content (Form) */}
          <form onSubmit={handleSubmit} className="feedback-form">
            <div className="feedback-modal-body">
              {/* Inquiry Type Buttons */}
              <div className="feedback-form-group">
                <label className="feedback-label">
                  Reason <span className="text-danger">*</span>
                </label>
                <div className="feedback-reason-buttons">
                  {INQUIRY_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`feedback-reason-btn ${
                        formData.inquiryType === type.value ? "active" : ""
                      }`}
                      onClick={() => updateField("inquiryType", type.value)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                {errors.inquiryType && (
                  <p className="feedback-error-message">
                    {errors.inquiryType}
                  </p>
                )}
              </div>

              {/* Module Select Field */}
              <div className="feedback-form-group">
                <label htmlFor="module" className="feedback-label">
                  Module <span className="text-danger">*</span>
                </label>
                <div className="feedback-select-wrapper">
                  <Dropdown
                    options={MODULES}
                    isMulti={false}
                    showSearch={true}
                    selected={selectedModule || { value: "", label: "Choose a module" }}
                    onSelect={(option) => updateField("module", option.value)}
                  />
                </div>
                {errors.module && (
                  <p className="feedback-error-message">{errors.module}</p>
                )}
              </div>

              {/* Screenshots Upload */}
              <div className="feedback-form-group">
                <label className="feedback-label">
                  Screenshots <span className="text-muted">(optional)</span>
                </label>
                <div className="feedback-screenshots-upload">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    multiple
                    onChange={handleScreenshotChange}
                    className="feedback-file-input"
                    id="support-screenshots"
                  />
                  <label htmlFor="support-screenshots" className="feedback-file-label">
                    <i className="fas fa-camera"></i>
                    <span>Add Screenshots</span>
                  </label>
                  {screenshots.length > 0 && (
                    <div className="feedback-screenshots-preview">
                      {screenshots.map((shot) => (
                        <div key={shot.id} className="feedback-screenshot-item">
                          <img
                            src={shot.preview}
                            alt="Screenshot"
                            className="feedback-screenshot-img"
                          />
                          <button
                            type="button"
                            className="feedback-screenshot-remove"
                            onClick={() => handleRemoveScreenshot(shot.id)}
                            aria-label="Remove screenshot"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Description Field (Plain Textarea) */}
              <div className="feedback-form-group">
                <label htmlFor="description" className="feedback-label">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  id="description"
                  className={`feedback-textarea ${errors.description ? "has-error" : ""}`}
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="Enter your description..."
                  rows={8}
                />
                {errors.description && (
                  <p className="feedback-error-message">{errors.description}</p>
                )}
              </div>

            </div>

            {/* Modal Footer */}
            <div className="feedback-modal-footer">
              <button
                type="submit"
                className="feedback-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
